import { formatNumberWithSuffix } from "../Ownership/utils";
import type { ShariahScreeningResponse } from "../../../../../../../api/services/shariah-screening.type";

export type ScreeningMetricId =
  | "business-activity"
  | "interest-securities"
  | "interest-debt";

export const FINANCIAL_COMPLIANT_THRESHOLD = 30;
export const BUSINESS_COMPLIANT_THRESHOLD = 5;

export const formatPercent = (value: number): string =>
  `${value.toFixed(2)}%`;

export const formatCompactUsd = (value: number): string =>
  formatNumberWithSuffix(value);

export function formatLastScanned(
  iso: string | undefined,
  locale: string,
): string | null {
  if (typeof iso !== "string" || !iso.trim()) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

export const deriveMarketCap = (
  dollarAmount: number,
  ratioPercent: number,
): number => {
  if (!Number.isFinite(dollarAmount) || !Number.isFinite(ratioPercent)) {
    return 0;
  }
  if (ratioPercent <= 0) return 0;
  return dollarAmount / (ratioPercent / 100);
};

export const formatRatioFormula = (
  numerator: number,
  denominator: number,
  resultPercent: number,
): string =>
  `${formatCompactUsd(numerator)} / ${formatCompactUsd(denominator)} = ${formatPercent(resultPercent)}`;

export interface RevenueSourceLine {
  name: string;
  pct: string;
}

function mapRevenueSourceItem(item: unknown): RevenueSourceLine {
  if (typeof item !== "object" || item === null) {
    return { name: String(item), pct: "—" };
  }
  const record = item as Record<string, unknown>;
  const name =
    (typeof record.name === "string" && record.name) ||
    (typeof record.label === "string" && record.label) ||
    "—";
  const pctValue =
    record.percentage ?? record.pct ?? record.percent ?? record.value;
  const pct =
    typeof pctValue === "number"
      ? formatPercent(pctValue)
      : typeof pctValue === "string"
        ? pctValue.includes("%")
          ? pctValue
          : `${pctValue}%`
        : "—";
  return { name, pct };
}

function mapRevenueSourceItems(parsed: unknown): RevenueSourceLine[] {
  if (!Array.isArray(parsed)) return [];
  return parsed.map(mapRevenueSourceItem);
}

/** API may return JSON strings or already-parsed arrays. */
export function parseRevenueSource(raw: unknown): RevenueSourceLine[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) return mapRevenueSourceItems(raw);
  if (typeof raw !== "string") return [];
  if (!raw.trim()) return [];
  try {
    return mapRevenueSourceItems(JSON.parse(raw));
  } catch {
    return [];
  }
}

export function getBusinessActivityPass(data: ShariahScreeningResponse): boolean {
  return (
    data.doubtful_revenue_perc + data.non_compliant_revenue_perc <=
    BUSINESS_COMPLIANT_THRESHOLD
  );
}

export function getDebtRatioPass(data: ShariahScreeningResponse): boolean {
  return data.debt_ratio < FINANCIAL_COMPLIANT_THRESHOLD;
}

export function getLiquidityRatioPass(data: ShariahScreeningResponse): boolean {
  return data.liquidity_ratio < FINANCIAL_COMPLIANT_THRESHOLD;
}

export function isFinancialRatioCompliant(ratioPercent: number): boolean {
  return ratioPercent < FINANCIAL_COMPLIANT_THRESHOLD;
}

export function getTotalRevenueDollar(data: ShariahScreeningResponse): number {
  return (
    data.compliant_revenue_dollar +
    data.doubtful_revenue_dollar +
    data.non_compliant_revenue_dollar
  );
}

export function getNotHalalBusinessActivityPercent(
  data: ShariahScreeningResponse,
): number {
  return data.doubtful_revenue_perc + data.non_compliant_revenue_perc;
}

export function getNotHalalBusinessActivityDollar(
  data: ShariahScreeningResponse,
): number {
  return data.doubtful_revenue_dollar + data.non_compliant_revenue_dollar;
}
